import React, { useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import {
  useEducationFeeSummary,
  useIssuedStockRequisitions,
  useLowStockItems,
  useOverdueEducationInvoices,
  useStockConsumptionLedger
} from "../../../hooks/api/education/useReports";
import { useStockItems } from "../../../hooks/api/education/useStockItem";

const EducationReportsPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const [tab, setTab] = useState(0);
  const { data: feeSummary, isPending: feePending } = useEducationFeeSummary();
  const { data: overdue = [], isPending: overduePending } =
    useOverdueEducationInvoices(100);
  const { data: lowStock = [], isPending: lowStockPending } =
    useLowStockItems(100);
  const { data: requisitions = [], isPending: reqPending } =
    useIssuedStockRequisitions();
  const { data: stockItems = [] } = useStockItems({ size: 100 });
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const { data: consumption = [], isPending: ledgerPending } =
    useStockConsumptionLedger(selectedItemId || undefined);

  if (feePending && tab === 0) {
    return (
      <CommonLoader
        loadingMessage={t("loading-reports", "Loading reports...")}
      />
    );
  }

  return (
    <Box data-testid="education-reports-page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("education-reports-title", "Reports")}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t("fee-reports-tab", "Fees")} />
        <Tab label={t("stock-reports-tab", "Stock")} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("fee-collection-summary", "Fee collection summary")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 2,
              mb: 3
            }}
          >
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("collected", "Collected")}
              </Typography>
              <Typography variant="h6">
                {feeSummary?.totalCollected ?? 0}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("outstanding", "Outstanding")}
              </Typography>
              <Typography variant="h6">
                {feeSummary?.totalOutstanding ?? 0}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("fee-bills", "Fee bills")}
              </Typography>
              <Typography variant="h6">
                {feeSummary?.feeBillCount ?? 0}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("overdue-count", "Overdue")}
              </Typography>
              <Typography variant="h6">
                {feeSummary?.overdueCount ?? 0}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("outstanding-fees-report", "Outstanding fee bills")}
          </Typography>
          {overduePending ? (
            <CommonLoader loadingMessage={t("loading", "Loading...")} />
          ) : overdue.length === 0 ? (
            <Typography color="text.secondary">
              {t("no-outstanding-fees", "No outstanding fee bills.")}
            </Typography>
          ) : (
            overdue.map((inv) => (
              <Box
                key={inv.invoiceId}
                sx={{
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider"
                }}
              >
                <Typography fontWeight={600}>{inv.invoiceNumber}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("student-id-label", "Student")}: {inv.studentId} ·{" "}
                  {inv.remainingAmount} · {inv.dueDate}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("low-stock-report", "Low stock items")}
          </Typography>
          {lowStockPending ? (
            <CommonLoader loadingMessage={t("loading", "Loading...")} />
          ) : lowStock.length === 0 ? (
            <Typography color="text.secondary">
              {t("no-low-stock", "No low-stock items.")}
            </Typography>
          ) : (
            lowStock.map((item) => (
              <Box
                key={item.id}
                sx={{
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider"
                }}
              >
                <Typography fontWeight={600}>{item.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.quantityOnHand} / {item.minimumQuantity} {item.baseUnit}
                </Typography>
              </Box>
            ))
          )}

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {t("stock-usage-report", "Issued requisitions (consumption)")}
          </Typography>
          {reqPending ? (
            <CommonLoader loadingMessage={t("loading", "Loading...")} />
          ) : requisitions.length === 0 ? (
            <Typography color="text.secondary">
              {t("no-issued-requisitions", "No issued requisitions yet.")}
            </Typography>
          ) : (
            requisitions.map((req) => (
              <Box
                key={req.id}
                sx={{
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider"
                }}
              >
                <Typography fontWeight={600}>{req.orderNumber}</Typography>
                {req.lines?.map((line, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    {line.stockItemName} × {line.quantity}
                  </Typography>
                ))}
              </Box>
            ))
          )}

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {t("consumption-ledger", "Stock-out ledger")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {stockItems.map((item) => (
              <Box
                key={item.id}
                component="button"
                onClick={() => setSelectedItemId(item.id ?? "")}
                sx={{
                  border: "1px solid",
                  borderColor:
                    selectedItemId === item.id ? "primary.main" : "divider",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  cursor: "pointer",
                  bgcolor: "background.paper"
                }}
              >
                {item.name}
              </Box>
            ))}
          </Box>
          {!selectedItemId ? (
            <Typography color="text.secondary">
              {t(
                "select-stock-item",
                "Select a stock item to view consumption."
              )}
            </Typography>
          ) : ledgerPending ? (
            <CommonLoader loadingMessage={t("loading", "Loading...")} />
          ) : consumption.length === 0 ? (
            <Typography color="text.secondary">
              {t("no-consumption", "No stock-out movements.")}
            </Typography>
          ) : (
            consumption.map((m) => (
              <Box
                key={m.id}
                sx={{
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider"
                }}
              >
                <Typography variant="body2">
                  {m.quantity} · {m.notes ?? m.movementType} · {m.createdAt}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default EducationReportsPage;
